export enum ChatEnum {
  CHAT = 'chat',
  PROGRAMMED_MESSAGES = 'programmed_messages',
  QUICK_ACTIONS = 'quick_actions',
  CREATE_QUICK_ACTION = 'create_quick_action',
  NOTES = 'notes',
  EVENTS = 'events',
  DRIVE = 'drive',
  MAPS = 'maps',
  TEMPLATES = 'templates',
  WABTEMPLATES = 'wabtemplates',
  LEADSALES_AI = 'leadsales_ai',
  WABTEMPLATES_VARIABLES = 'wabtemplates_variables',
  WABTEMPLATES_SCHEDULED = 'wabtemplates_scheduled',
  WAPI_SCHEDULE_SELECTOR = 'wapi_schedule_selector',
  INTERACTIVE = 'interactive',
}

/**
 + * Represents the various states a chat can be in.
 + * Used for controlling UI behavior and enforcing meta rules restrictions.
 + */
export enum ChatState {
  /** Chat is active and fully functional */
  ACTIVE = 'ACTIVE',
  /** Chat is in template mode with restricted functionality */
  TEMPLATE = 'TEMPLATE',
  /** Chat is inactive and cannot be used */
  INACTIVE = 'INACTIVE',
  /** Chat has expired and cannot be used */
  EXPIRED = 'EXPIRED',
}

export enum ChatMessageContainerEnum {
  CHAT = 'chat',
  RECORD = 'record',
}

export enum ChatTextAreaContainerEnum {
  TEXTAREA = 'textarea',
}

export enum NoteType {
  DEFAULT = 'DEFAULT',
  SOLD = 'SOLD',
  LOST = 'LOST',
  ENDED = 'ENDED',
}

export enum CalendarEventType {
  CALL = 'CALL',
  MEETING = 'MEETING',
  TASK = 'TASK',
}

export enum MessageErrorType {
  NO_ERROR = '',
  PROVIDER_SEND_ERROR = 'PROVIDER_SEND_ERROR',
  UNKNOWN_ERROR_FROM_PROVIDER = 'UNKNOWN_ERROR_FROM_PROVIDER',
  MESSAGE_NOT_SUPPORTED = 'MESSAGE_NOT_SUPPORTED',
  MESSAGE_DECRYPTION_ERROR = 'MESSAGE_DECRYPTION_ERROR',
  MESSAGE_NOT_FOUND_ON_PROVIDER = 'MESSAGE_NOT_FOUND_ON_PROVIDER',
  MESSAGE_REJECTED_BY_PROVIDER = 'MESSAGE_REJECTED_BY_PROVIDER',
  EMPTY_MESSAGE_ERROR = 'EMPTY_MESSAGE_ERROR',
  MESSAGE_WINDOW_CLOSED = 'MESSAGE_WINDOW_CLOSED',
  RECIPIENT_UNAVAILABLE = 'RECIPIENT_UNAVAILABLE',
  AUTH_INVALID_TOKEN_ERROR = 'AUTH_INVALID_TOKEN_ERROR',
  MEDIA_UPLOAD_ERROR = 'MEDIA_UPLOAD_ERROR',
  // Chat codes 1016-1018: the send was refused before reaching any provider.
  // The values match the HTTP refusal codes, so a row and a refused request
  // resolve through the same lookup (lookupOutboundRefusalByCode).
  WORKSPACE_PLAN_PAUSED = 'WORKSPACE_PLAN_PAUSED',
  SENDER_WORKSPACE_MISMATCH = 'SENDER_WORKSPACE_MISMATCH',
  SENDER_WORKSPACE_UNRESOLVABLE = 'SENDER_WORKSPACE_UNRESOLVABLE',
  ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP = 'ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP',
  RECIPIENT_NOT_FOUND_ON_PROVIDER = 'RECIPIENT_NOT_FOUND_ON_PROVIDER',
  EMPTY_MESSAGE = 'EMPTY_MESSAGE',
  PINNED_MESSAGE_NOTIFICATION_NOT_SUPPORTED = 'PINNED_MESSAGE_NOTIFICATION_NOT_SUPPORTED',
  PLACEHOLDER_MESSAGE_NOT_SUPPORTED = 'PLACEHOLDER_MESSAGE_NOT_SUPPORTED',
  TEMPLATE_MESSAGE_NOT_SUPPORTED = 'TEMPLATE_MESSAGE_NOT_SUPPORTED',
  INTERACTIVE_MESSAGE_NOT_SUPPORTED = 'INTERACTIVE_MESSAGE_NOT_SUPPORTED',
  LOTTIE_STICKER_NOT_SUPPORTED = 'LOTTIE_STICKER_NOT_SUPPORTED',
  GROUP_MENTION_IN_STATUS_NOTIFICATION_NOT_SUPPORTED = 'GROUP_MENTION_IN_STATUS_NOTIFICATION_NOT_SUPPORTED',
  GROUP_INVITE_MESSAGE_NOT_SUPPORTED = 'GROUP_INVITE_MESSAGE_NOT_SUPPORTED',
  DEVICE_SENT_MESSAGE_NOT_SUPPORTED = 'DEVICE_SENT_MESSAGE_NOT_SUPPORTED',
  COMMUNITY_ANNOUNCEMENT_COMMENT_NOT_SUPPORTED = 'COMMUNITY_ANNOUNCEMENT_COMMENT_NOT_SUPPORTED',
  OTHER_MESSAGE_TYPE_NOT_SUPPORTED = 'OTHER_MESSAGE_TYPE_NOT_SUPPORTED',
  WHATSAPP_WEB_ACCOUNT_DISCONNECTED = 'WHATSAPP_WEB_ACCOUNT_DISCONNECTED',
  WHATSAPP_WEB_NEW_CHAT_LIMIT_REACHED = 'WHATSAPP_WEB_NEW_CHAT_LIMIT_REACHED',
  WAPI_CREDIT_CARD_EXPIRED_ERROR = 'WAPI_CREDIT_CARD_EXPIRED_ERROR',
  WAPI_API_UNKNOWN_ERROR = 'WAPI_API_UNKNOWN_ERROR',
  WAPI_SERVICE_UNAVAILABLE_ERROR = 'WAPI_SERVICE_UNAVAILABLE_ERROR',
  WAPI_INVALID_PARAM_VALUE_ERROR = 'WAPI_INVALID_PARAM_VALUE_ERROR',
  WAPI_INVALID_PARAMETER_ERROR = 'WAPI_INVALID_PARAMETER_ERROR',
  WAPI_ACCESS_TOKEN_ERROR = 'WAPI_ACCESS_TOKEN_ERROR',
  WAPI_PERMISSION_ERROR = 'WAPI_PERMISSION_ERROR',
  WAPI_BUSINESS_VERIFICATION_REQUIRED_ERROR = 'WAPI_BUSINESS_VERIFICATION_REQUIRED_ERROR',
  WAPI_PHONE_NUMBER_VERIFICATION_REQUIRED_ERROR = 'WAPI_PHONE_NUMBER_VERIFICATION_REQUIRED_ERROR',
  WAPI_TEMPLATE_NOT_FOUND_ERROR = 'WAPI_TEMPLATE_NOT_FOUND_ERROR',
  WAPI_TEMPORARILY_BLOCKED_ERROR = 'WAPI_TEMPORARILY_BLOCKED_ERROR',
  WAPI_UNKNOWN_ERROR = 'WAPI_UNKNOWN_ERROR',
  WAPI_GENERAL_FAILURE_ERROR = 'WAPI_GENERAL_FAILURE_ERROR',
  WAPI_ACCESS_DENIED_ERROR = 'WAPI_ACCESS_DENIED_ERROR',
  WAPI_MISSING_PARAMETER_ERROR = 'WAPI_MISSING_PARAMETER_ERROR',
  WAPI_SERVICE_UNAVAILABLE_ERROR_2 = 'WAPI_SERVICE_UNAVAILABLE_ERROR_2',
  WAPI_RECIPIENT_SENDER_CONFLICT_ERROR = 'WAPI_RECIPIENT_SENDER_CONFLICT_ERROR',
  WAPI_MESSAGE_UNDELIVERABLE_ERROR = 'WAPI_MESSAGE_UNDELIVERABLE_ERROR',
  WAPI_DISCONNECTED_ACCOUNT_ERROR = 'WAPI_DISCONNECTED_ACCOUNT_ERROR',
  WAPI_DISPLAY_NAME_PENDING_ERROR = 'WAPI_DISPLAY_NAME_PENDING_ERROR',
  WAPI_BUSINESS_ISSUE_ERROR = 'WAPI_BUSINESS_ISSUE_ERROR',
  WAPI_CERTIFICATE_ERROR = 'WAPI_CERTIFICATE_ERROR',
  WAPI_REENGAGEMENT_REQUIRED_ERROR = 'WAPI_REENGAGEMENT_REQUIRED_ERROR',
  WAPI_RATE_LIMITED_ERROR = 'WAPI_RATE_LIMITED_ERROR',
  WAPI_HEALTHY_ENGAGEMENT_ERROR = 'WAPI_HEALTHY_ENGAGEMENT_ERROR',
  WAPI_USER_STOPPED_MARKETING_ERROR = 'WAPI_USER_STOPPED_MARKETING_ERROR',
  WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR = 'WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR',
  // BUG-704: 131051 subtype refinement. 3063 (edit) and 3064 (generic minor
  // subtype) render as calm informational notes — NOT the orange error rail —
  // and are gated out of showCheckStatus + per-message affordances. 3050 keeps
  // its rail with rewritten honest copy. See isWapiUnsupportedInfoNote.
  WAPI_EDITED_MESSAGE_ERROR = 'WAPI_EDITED_MESSAGE_ERROR',
  WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR = 'WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR',
  // INT-1214: send rejected by the per-number throughput limiter (INT-1205).
  WAPI_SEND_RATE_LIMITED_ERROR = 'WAPI_SEND_RATE_LIMITED_ERROR',
  // INT-1011: the attachment could not be uploaded to WhatsApp before sending
  // (strict media-upload mode); the message was not sent and retrying works.
  WAPI_OUTBOUND_MEDIA_UPLOAD_ERROR = 'WAPI_OUTBOUND_MEDIA_UPLOAD_ERROR',
  // BUG-908 U12: a claimed retry whose dispatch could not be confirmed.
  // Delivery is genuinely unknown, so this is not one of the send failures
  // above and its copy sends the advisor to look at the chat.
  WAPI_RETRY_UNCONFIRMED = 'WAPI_RETRY_UNCONFIRMED',
  // INT-1500 (contract C2): same names as messaging ErrorCodes 3073, 3075-3081.
  // Meta's own (the chat's Meta error notice explains them):
  WAPI_NUMBER_NOT_REGISTERED_ERROR = 'WAPI_NUMBER_NOT_REGISTERED_ERROR',
  WAPI_MARKETING_DISABLED_ERROR = 'WAPI_MARKETING_DISABLED_ERROR',
  // Sends Leadsales refused before they reached Meta (see isLeadsalesTemplateRefusal):
  WAPI_SCHEDULED_TEMPLATE_DELETED_ERROR = 'WAPI_SCHEDULED_TEMPLATE_DELETED_ERROR',
  WAPI_MARKETING_BACKOFF_ERROR = 'WAPI_MARKETING_BACKOFF_ERROR',
  WAPI_MARKETING_OPT_OUT_ERROR = 'WAPI_MARKETING_OPT_OUT_ERROR',
  WAPI_MARKETING_PAUSED_RESTRICTED_ERROR = 'WAPI_MARKETING_PAUSED_RESTRICTED_ERROR',
  WAPI_TEMPLATE_CREDITS_ERROR = 'WAPI_TEMPLATE_CREDITS_ERROR',
  WAPI_TEMPLATE_EMPTY_VARIABLES_ERROR = 'WAPI_TEMPLATE_EMPTY_VARIABLES_ERROR',
  WAPI_MEDIA_DOWNLOAD_ERROR = 'WAPI_MEDIA_DOWNLOAD_ERROR',
  WAPI_MEDIA_UPLOAD_ERROR = 'WAPI_MEDIA_UPLOAD_ERROR',
  WAPI_PAIR_RATE_LIMIT_ERROR = 'WAPI_PAIR_RATE_LIMIT_ERROR',
  WAPI_CONCURRENCY_LIMIT_ERROR = 'WAPI_CONCURRENCY_LIMIT_ERROR',
  WAPI_TEMPLATE_PARAM_COUNT_ERROR = 'WAPI_TEMPLATE_PARAM_COUNT_ERROR',
  WAPI_TEMPLATE_NOT_APPROVED_ERROR = 'WAPI_TEMPLATE_NOT_APPROVED_ERROR',
  WAPI_HYDRATED_TEXT_TOO_LONG_ERROR = 'WAPI_HYDRATED_TEXT_TOO_LONG_ERROR',
  WAPI_FORMAT_POLICY_VIOLATION_ERROR = 'WAPI_FORMAT_POLICY_VIOLATION_ERROR',
  WAPI_PARAMETER_FORMAT_MISMATCH_ERROR = 'WAPI_PARAMETER_FORMAT_MISMATCH_ERROR',
  WAPI_TEMPLATE_PAUSED_ERROR = 'WAPI_TEMPLATE_PAUSED_ERROR',
  WAPI_TEMPLATE_DISABLED_ERROR = 'WAPI_TEMPLATE_DISABLED_ERROR',
  WAPI_TEMPLATE_INVALID_PARAMS_ERROR = 'WAPI_TEMPLATE_INVALID_PARAMS_ERROR',
  WAPI_INCOMPLETE_DEREGISTRATION_ERROR = 'WAPI_INCOMPLETE_DEREGISTRATION_ERROR',
  WAPI_REGISTRATION_SERVER_UNAVAILABLE_ERROR = 'WAPI_REGISTRATION_SERVER_UNAVAILABLE_ERROR',
  WAPI_DUPLICATE_LANGUAGE_CONTENT_ERROR = 'WAPI_DUPLICATE_LANGUAGE_CONTENT_ERROR',
  WAPI_TEMPLATE_LIMIT_EXCEEDED_ERROR = 'WAPI_TEMPLATE_LIMIT_EXCEEDED_ERROR',
  WAPI_CHARACTER_LIMIT_EXCEEDED_ERROR = 'WAPI_CHARACTER_LIMIT_EXCEEDED_ERROR',
  WAPI_MISSING_FIELDS_ERROR = 'WAPI_MISSING_FIELDS_ERROR',
  WAPI_HEADER_FORMAT_ERROR = 'WAPI_HEADER_FORMAT_ERROR',
  WAPI_BODY_FORMAT_ERROR = 'WAPI_BODY_FORMAT_ERROR',
  WAPI_FOOTER_FORMAT_ERROR = 'WAPI_FOOTER_FORMAT_ERROR',
  WAPI_TEMPLATE_RECENTLY_DELETED_ERROR = 'WAPI_TEMPLATE_RECENTLY_DELETED_ERROR',
  WAPI_UNSUPPORTED_MESSAGE_ERROR = 'WAPI_UNSUPPORTED_MESSAGE_ERROR',
  WAPI_POSSIBLE_UNSUPPORTED_DEVICE_ERROR = 'WAPI_POSSIBLE_UNSUPPORTED_DEVICE_ERROR',
  WAPI_CLOUD_API_NOT_AVAILABLE_ERROR = 'WAPI_CLOUD_API_NOT_AVAILABLE_ERROR',
  WAPI_HELD_FOR_QUALITY_ERROR = 'WAPI_HELD_FOR_QUALITY_ERROR',
  // BUG-548: 11 errors promoted out of the wba-10026 fallback. Each used to
  // surface as the generic "Meta no puede visualizar este mensaje" tooltip
  // because `renderMessageNotSupported` had no dedicated case.
  WAPI_AUTH_EXCEPTION_ERROR = 'WAPI_AUTH_EXCEPTION_ERROR',
  WAPI_API_METHOD_ERROR = 'WAPI_API_METHOD_ERROR',
  WAPI_PERMISSION_DENIED_ERROR = 'WAPI_PERMISSION_DENIED_ERROR',
  WAPI_ACCOUNT_RATE_LIMIT_ERROR = 'WAPI_ACCOUNT_RATE_LIMIT_ERROR',
  WAPI_BUSINESS_BLOCKED_USER_ERROR = 'WAPI_BUSINESS_BLOCKED_USER_ERROR',
  WAPI_RATE_LIMIT_HIT_ERROR = 'WAPI_RATE_LIMIT_HIT_ERROR',
  WAPI_RECIPIENT_EXPERIMENT_ERROR = 'WAPI_RECIPIENT_EXPERIMENT_ERROR',
  WAPI_MESSAGING_RESTRICTED_COUNTRY_ERROR = 'WAPI_MESSAGING_RESTRICTED_COUNTRY_ERROR',
  WAPI_RECIPIENT_NOT_ALLOWED_ERROR = 'WAPI_RECIPIENT_NOT_ALLOWED_ERROR',
  WAPI_TEMPLATE_WORD_RATIO_ERROR = 'WAPI_TEMPLATE_WORD_RATIO_ERROR',
  WAPI_TEMPLATE_LEADING_TRAILING_PARAM_ERROR = 'WAPI_TEMPLATE_LEADING_TRAILING_PARAM_ERROR',
  TIKTOK_PROVIDER_REJECTED = 'TIKTOK_PROVIDER_REJECTED',
  TIKTOK_UNSUPPORTED_ATTACHMENT_TYPE = 'TIKTOK_UNSUPPORTED_ATTACHMENT_TYPE',
  TIKTOK_IMAGE_NOT_SUPPORTED = 'TIKTOK_IMAGE_NOT_SUPPORTED',
  TIKTOK_MESSAGE_WINDOW_RESTRICTED = 'TIKTOK_MESSAGE_WINDOW_RESTRICTED',
  META_INACTIVITY_WINDOW_EXPIRED = 'META_INACTIVITY_WINDOW_EXPIRED',
  META_SEND_UNCONFIRMED = 'META_SEND_UNCONFIRMED',
  // INT-1437. These strings must match the BFF enums verbatim
  // (bff-web and bff-mobile src/chat/chat.enums.go): the history path matches
  // on the string, and a typo falls back to UNKNOWN_ERROR_FROM_PROVIDER with
  // the generic copy -- the same symptom as the bug, and just as invisible.
  META_MESSAGE_TOO_LONG = 'META_MESSAGE_TOO_LONG',
  META_ATTACHMENT_UNSUPPORTED = 'META_ATTACHMENT_UNSUPPORTED',
  META_ATTACHMENT_UPLOAD_FAILED = 'META_ATTACHMENT_UPLOAD_FAILED',
  META_THREAD_CONTROL_LOST = 'META_THREAD_CONTROL_LOST',
  META_ATTACHMENT_FETCH_FAILED = 'META_ATTACHMENT_FETCH_FAILED',
  META_ATTACHMENT_TOO_LARGE = 'META_ATTACHMENT_TOO_LARGE',
  WAPI_WALLET_INSUFFICIENT_BALANCE_ERROR = 'WAPI_WALLET_INSUFFICIENT_BALANCE_ERROR',
  WAPI_WALLET_UNAVAILABLE_ERROR = 'WAPI_WALLET_UNAVAILABLE_ERROR',
  WAPI_WALLET_UNPRICEABLE_ERROR = 'WAPI_WALLET_UNPRICEABLE_ERROR',
  // BUG-1057: same name as messaging ErrorCodes 3082 (wba-10125). A send
  // Leadsales refused before Meta: the workspace has no WhatsApp Business API
  // account to send from.
  WAPI_ACCOUNT_NOT_CONNECTED_ERROR = 'WAPI_ACCOUNT_NOT_CONNECTED_ERROR',
  // BUG-1104: same names as messaging ErrorCodes 3083 (wba-10126) and 3084
  // (wba-10127). Sends Leadsales refused before Meta: a sticker that misses
  // Meta's sticker limits, and a message type Leadsales cannot send through
  // WhatsApp API.
  WAPI_STICKER_NOT_SENDABLE_ERROR = 'WAPI_STICKER_NOT_SENDABLE_ERROR',
  WAPI_MESSAGE_TYPE_NOT_SENDABLE_ERROR = 'WAPI_MESSAGE_TYPE_NOT_SENDABLE_ERROR',
  // INT-1683: same name as messaging ErrorCodes 3085 (wba-10128). An image
  // Leadsales refused before Meta because WhatsApp would refuse it; the reason
  // token travels in the error details.
  WAPI_IMAGE_NOT_SENDABLE_ERROR = 'WAPI_IMAGE_NOT_SENDABLE_ERROR',
}

export enum MediaType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  DOCUMENT = 'DOCUMENT',
  LOCATION = 'LOCATION',
  STICKER = 'STICKER',
  CONTACT = 'CONTACT',
  PDF = 'PDF',
  CSV = 'CSV',
  // SHARE_POST: TikTok shared video posts (link-preview card).
  // See INT-929 / U3 plan. No top-level `url` — fields ride on optional
  // SHARE_POST-specific properties of `tAttachment`.
  SHARE_POST = 'SHARE_POST',
}

export enum MessageState {
  CREATED = 'CREATED',
  SEND_PENDING = 'SEND_PENDING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  DECRYPTING = 'DECRYPTING',
  DELIVERED = 'DELIVERED',
  READ = 'READ',
  ERROR = 'ERROR',
}

export enum EventType {
  NOTE = 'NOTE',
  MESSAGE = 'MESSAGE',
  FOLLOW_UP = 'FOLLOW_UP',
  OUTBOUND_DELETED = 'OUTBOUND_DELETED',
  INBOUND_DELETED = 'INBOUND_DELETED',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
  TEMPLATE = 'TEMPLATE',
  LEADSALES_AI = 'LEADSALES_AI',
  SYSTEM = 'SYSTEM',
}

export enum Integrations {
  WHATSAPP_WEB = 'WHATSAPP_WEB',
  MESSENGER = 'MESSENGER',
  INSTAGRAM = 'INSTAGRAM',
}

export enum LeadStatusSpanish {
  IMPORTANT = 'Importante',
  PENDING = 'Pendiente',
  STUCK = 'Atorado',
  URGENT = 'Urgente',
  NEUTRAL = 'Neutral',
}

export enum eField {
  NAME = 'NAME',
  PROFILE_PICTURE = 'PROFILE_PICTURE',
  EMAIL = 'EMAIL',
  COMPANY = 'COMPANY',
  ASSIGNEE_AUTH_ID = 'ASSIGNEE_AUTH_ID',
  ARCHIVED = 'ARCHIVED',
  VALUE = 'VALUE',
  STAGE = 'STAGE',
  FUNNEL = 'FUNNEL',
  FOLLOW_UP = 'FOLLOW_UP',
  MESSAGE = 'MESSAGE',
  NOTE = 'NOTE',
  IN_INBOX = 'IN_INBOX',
  LEAD = 'LEAD',
}

export enum eAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  EXECUTE = 'EXECUTE',
}

